import { Signal } from 'signal-polyfill';
import type { FormMetadataDto, TermsDto, TrialEventSummaryDto } from '../../../api/types';
import { formActions, formState, hasSelections, isFormBusy, isFormValid } from '../../../state/form.state';

export type RegistrationPageState = 'loading' | 'loaded' | 'error';

const trial = new Signal.State<TrialEventSummaryDto | null>(null);
const formMetadata = new Signal.State<FormMetadataDto | null>(null);
const terms = new Signal.State<TermsDto | null>(null);
const loading = new Signal.State(false);
const error = new Signal.State<string | null>(null);
const validationSummaryVisible = new Signal.State(false);
const touchedFields = new Signal.State<Set<string>>(new Set());
const submitErrorMessage = new Signal.State('');
const submitSupportId = new Signal.State('');

const pageState = new Signal.Computed<RegistrationPageState>(() => {
  if (loading.get()) return 'loading';
  if (error.get()) return 'error';
  return 'loaded';
});

const termsAccepted = new Signal.Computed(() => formState.get().terms.accepted);
const termsVersion = new Signal.Computed(() => formState.get().terms.version);

const canSubmit = new Signal.Computed(() => {
  return (
    isFormValid.get() &&
    hasSelections.get() &&
    termsAccepted.get() &&
    !isFormBusy.get()
  );
});

export const registrationState = {
  trial,
  formMetadata,
  terms,
  loading,
  error,
  validationSummaryVisible,
  touchedFields,
  submitErrorMessage,
  submitSupportId,
};

export const registrationActions = {
  reset(): void {
    trial.set(null);
    formMetadata.set(null);
    terms.set(null);
    loading.set(false);
    error.set(null);
    validationSummaryVisible.set(false);
    touchedFields.set(new Set());
    submitErrorMessage.set('');
    submitSupportId.set('');
    formActions.updateTerms({ accepted: false, version: '' });
  },

  setTrial(value: TrialEventSummaryDto | null): void {
    trial.set(value);
  },

  setFormMetadata(value: FormMetadataDto | null): void {
    formMetadata.set(value);
  },

  setTerms(value: TermsDto | null): void {
    terms.set(value);
    if (value) {
      const wasDirty = formState.get().isDirty;
      formActions.updateTerms({ version: value.version });
      if (!wasDirty) {
        formActions.markClean();
      }
    }
  },

  setLoading(value: boolean): void {
    loading.set(value);
  },

  setError(value: string | null): void {
    error.set(value);
  },

  setValidationSummaryVisible(value: boolean): void {
    validationSummaryVisible.set(value);
  },

  markTouched(path: string): void {
    const next = new Set(touchedFields.get());
    next.add(path);
    touchedFields.set(next);
  },

  clearTouched(): void {
    touchedFields.set(new Set());
  },

  setSubmitError(message: string, supportId = ''): void {
    submitErrorMessage.set(message);
    submitSupportId.set(supportId);
  },

  clearSubmitError(): void {
    submitErrorMessage.set('');
    submitSupportId.set('');
  },
};

export const registrationComputed = {
  pageState,
  termsAccepted,
  termsVersion,
  canSubmit,
};

export type RegistrationState = typeof registrationState;
